import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { useAuth } from "../../context/AuthContext";
import { createHackathon, updateHackathon, getHackathon } from "../../services/hackathon.service";
import { toast } from "sonner";

interface Round {
  order: number;
  name: string;
  type: string;
  startDate: string;
  endDate: string;
}

interface Theme {
  title: string;
  description: string;
}

interface Prize {
  title: string;
  amount: string;
  currency: string;
}

export function ManageHackathon() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { token } = useAuth();
  const isEdit = Boolean(id);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [error, setError] = useState<string | null>(null);
  const [isCompleted, setIsCompleted] = useState(false);

  const [formData, setFormData] = useState({
    title: "",
    organizerName: "",
    description: "",
    startDate: "",
    endDate: "",
    mode: "online",
    // Registration phase
    registrationStart: "",
    registrationEnd: "",
    // Submission phase
    submissionStart: "",
    submissionEnd: "",
    submissionInitialEnd: "",
    submissionFinalEnd: "",
    mentorReviewDays: "",
    ideaSelectionDeadline: "",
    // Evaluation phase
    evaluationStart: "",
    evaluationEnd: "",
    maxTeamSize: "5",
  });

  const [themes, setThemes] = useState<Theme[]>([{ title: "", description: "" }]);
  const [prizes, setPrizes] = useState<Prize[]>([{ title: "", amount: "", currency: "INR" }]);
  const [rulesFile, setRulesFile] = useState<File | null>(null);

  const [rounds, setRounds] = useState<Round[]>([
    {
      order: 1,
      name: "",
      type: "",
      startDate: "",
      endDate: "",
    },
  ]);

  // Load existing hackathon data when in edit mode
  useEffect(() => {
    if (!isEdit || !id || !token) return;
    
    const loadHackathon = async () => {
      try {
        setLoading(true);
        const data = await getHackathon(token, id);
        
        // Check if hackathon is completed
        const now = new Date();
        const endDate = new Date(data.endDate);
        if (now > endDate) {
          setIsCompleted(true);
          setError("This hackathon has been completed and cannot be edited.");
          setLoading(false);
          return;
        }
        
        // Populate form data
        setFormData({
          title: data.title || "",
          organizerName: data.organizerName || "",
          description: data.description || "",
          startDate: data.startDate?.split("T")[0] || "",
          endDate: data.endDate?.split("T")[0] || "",
          mode: data.mode || "online",
          registrationStart: data.phases?.registration?.startDate?.split("T")[0] || "",
          registrationEnd: data.phases?.registration?.endDate?.split("T")[0] || "",
          submissionStart: data.phases?.submission?.startDate?.split("T")[0] || "",
          submissionEnd: data.phases?.submission?.endDate?.split("T")[0] || "",
          submissionInitialEnd: data.phases?.submission?.initialSubmissionEnd?.split("T")[0] || "",
          submissionFinalEnd: data.phases?.submission?.finalSubmissionEnd?.split("T")[0] || "",
          mentorReviewDays: data.phases?.submission?.mentorReviewDays?.toString() || "",
          ideaSelectionDeadline: data.phases?.submission?.ideaSelection?.decisionDeadline?.split("T")[0] || "",
          evaluationStart: data.phases?.evaluation?.startDate?.split("T")[0] || "",
          evaluationEnd: data.phases?.evaluation?.endDate?.split("T")[0] || "",
          maxTeamSize: data.maxTeamSize?.toString() || "5",
        });

        // Populate themes
        if (data.themes && data.themes.length > 0) {
          setThemes(data.themes);
        }

        // Populate prizes
        if (data.prizes && data.prizes.length > 0) {
          setPrizes(data.prizes.map((p: any) => ({
            title: p.title || "",
            amount: p.amount?.toString() || "",
            currency: p.currency || "INR",
          })));
        }

        // Populate rounds
        if (data.phases?.evaluation?.rounds && data.phases.evaluation.rounds.length > 0) {
          setRounds(data.phases.evaluation.rounds.map((r: any) => ({
            order: r.order || 1,
            name: r.name || "",
            type: r.type || "",
            startDate: r.startDate?.split("T")[0] || "",
            endDate: r.endDate?.split("T")[0] || "",
          })));
        }

        setLoading(false);
      } catch (err: any) {
        setError(err.message);
        setLoading(false);
      }
    };

    loadHackathon();
  }, [isEdit, id, token]);

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleThemeChange = (index: number, field: keyof Theme, value: string) => {
    const updated = [...themes];
    updated[index] = { ...updated[index], [field]: value };
    setThemes(updated);
  };

  const addTheme = () => setThemes([...themes, { title: "", description: "" }]);
  const removeTheme = (index: number) => setThemes(themes.filter((_, i) => i !== index));

  const handlePrizeChange = (index: number, field: keyof Prize, value: string) => {
    const updated = [...prizes];
    updated[index] = { ...updated[index], [field]: value };
    setPrizes(updated);
  };

  const addPrize = () => setPrizes([...prizes, { title: "", amount: "", currency: "INR" }]);
  const removePrize = (index: number) => setPrizes(prizes.filter((_, i) => i !== index));

  const handleRoundChange = (index: number, field: keyof Round, value: string | number) => {
    const newRounds = [...rounds];
    newRounds[index] = { ...newRounds[index], [field]: value };
    setRounds(newRounds);
  };

  const addRound = () => {
    setRounds([
      ...rounds,
      {
        order: rounds.length + 1,
        name: "",
        type: "",
        startDate: "",
        endDate: "",
      },
    ]);
  };

  const removeRound = (index: number) => {
    const newRounds = rounds.filter((_, i) => i !== index);
    setRounds(newRounds);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    setSubmitting(true);
    setError(null);
    try {
      let rules: { fileName: string; fileUrl: string; fileType: string; fileSize: number } | undefined;
      if (rulesFile) {
        const fd = new FormData();
        fd.append("file", rulesFile);
        const uploadRes = await fetch(`${process.env.REACT_APP_API_URL}/hackathon-events/upload`, {
          method: "POST",
          body: fd,
        });
        if (!uploadRes.ok) throw new Error("File upload failed");
        rules = await uploadRes.json();
      }
      const payload = {
        title: formData.title,
        organizerName: formData.organizerName,
        description: formData.description,
        themes,
        startDate: formData.startDate,
        endDate: formData.endDate,
        mode: formData.mode,
        maxTeamSize: Number(formData.maxTeamSize),
        phases: {
          registration: {
            enabled: true,
            startDate: formData.registrationStart,
            endDate: formData.registrationEnd,
          },
          submission: {
            enabled: true,
            startDate: formData.submissionStart,
            endDate: formData.submissionEnd,
            initialSubmissionStart: formData.submissionStart,
            initialSubmissionEnd: formData.submissionInitialEnd,
            finalSubmissionStart: formData.submissionInitialEnd,
            finalSubmissionEnd: formData.submissionFinalEnd,
            mentorReviewDays: Number(formData.mentorReviewDays),
            ideaSelection: {
              panelId: null,
              decisionDeadline: formData.ideaSelectionDeadline,
            },
          },
          evaluation: {
            enabled: true,
            startDate: formData.evaluationStart,
            endDate: formData.evaluationEnd,
            rounds: rounds.map((r) => ({
              order: r.order,
              name: r.name,
              type: r.type,
              startDate: r.startDate,
              endDate: r.endDate,
              panelId: null,
            })),
          },
        },
        prizes: prizes.map((p) => ({ ...p, amount: Number(p.amount) })),
        ...(rules ? { rules } : {}),
      };
      if (isEdit && id) {
        await updateHackathon(token, id, payload);
        toast.success("Hackathon updated successfully!");
      } else {
        await createHackathon(token, payload);
        toast.success("Hackathon created successfully!");
      }
      navigate("/admin-dashboard/manage-hackathon");
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="px-[12px] py-[32px]">
      <div className="mb-[32px]">
        <h1 className="font-['Inter:Semi_Bold',sans-serif] font-semibold leading-[42px] text-[#1a2332] text-[28px]">
          {isEdit ? "Edit Hackathon" : "Create Hackathon"}
        </h1>
        <p className="font-['Inter:Regular',sans-serif] font-normal leading-[21px] text-[#6e7891] text-[14px]">
          Configure hackathon details, phases, and evaluation rounds.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <p className="text-[#6e7891] text-[14px]">Loading hackathon details...</p>
        </div>
      ) : isCompleted ? (
        <div className="rounded-[16px] border border-[#ff816e] bg-[#fff5f3] p-6">
          <p className="text-[#ff816e] font-medium text-[14px]">
            This hackathon has been completed and cannot be edited.
          </p>
          <button
            onClick={() => navigate("/admin-dashboard/manage-hackathon")}
            className="mt-4 px-[24px] py-[12px] bg-[#0058ab] text-white rounded-[14px] font-medium text-[14px] hover:bg-[#004a94] transition-colors"
          >
            Back to Hackathons
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="flex flex-col gap-[32px]">
        {/* Basic Information */}
        <FormSection title="Basic Information">
          <FormField label="Title" required>
            <Input
              value={formData.title}
              onChange={(e) => handleInputChange("title", e.target.value)}
              placeholder="Enter hackathon title"
              required
            />
          </FormField>

          <FormField label="Organizer Name" required>
            <Input
              value={formData.organizerName}
              onChange={(e) => handleInputChange("organizerName", e.target.value)}
              placeholder="Enter organizer name"
              required
            />
          </FormField>

          <FormField label="Description" required>
            <Textarea
              value={formData.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
              placeholder="Enter hackathon description"
              rows={4}
              required
            />
          </FormField>

          <FormField label="Themes">
            <div className="flex flex-col gap-[12px]">
              {themes.map((theme, index) => (
                <div key={index} className="flex flex-col gap-[8px] p-[16px] border border-[#e4e8ee] rounded-[12px]">
                  <div className="flex items-center justify-between">
                    <span className="text-[12px] text-[#6e7891]">Theme {index + 1}</span>
                    {themes.length > 1 && (
                      <button type="button" onClick={() => removeTheme(index)}
                        className="px-[10px] py-[4px] bg-[#ff816e] text-white rounded text-[11px] hover:bg-[#ff6b56] transition-colors">
                        Remove
                      </button>
                    )}
                  </div>
                  <Input value={theme.title} onChange={(e) => handleThemeChange(index, "title", e.target.value)} placeholder="Theme title" />
                  <Input value={theme.description} onChange={(e) => handleThemeChange(index, "description", e.target.value)} placeholder="Theme description" />
                </div>
              ))}
              <button type="button" onClick={addTheme}
                className="px-[16px] py-[8px] bg-white border border-[#0058ab] text-[#0058ab] rounded-sm text-[13px] hover:bg-[#f0f5ff] transition-colors self-start">
                Add Theme
              </button>
            </div>
          </FormField>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-[16px]">
            <FormField label="Start Date" required>
              <Input
                type="date"
                value={formData.startDate}
                onChange={(e) => handleInputChange("startDate", e.target.value)}
                required
              />
            </FormField>

            <FormField label="End Date" required>
              <Input
                type="date"
                value={formData.endDate}
                onChange={(e) => handleInputChange("endDate", e.target.value)}
                required
              />
            </FormField>
          </div>

          <FormField label="Mode" required>
            <Select value={formData.mode} onValueChange={(value) => handleInputChange("mode", value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-gray-50">
                <SelectItem value="online" className="hover:bg-gray-200 hover:cursor-pointer">Online</SelectItem>
                <SelectItem value="offline" className="hover:bg-gray-200 hover:cursor-pointer">Offline</SelectItem>
                <SelectItem value="hybrid" className="hover:bg-gray-200 hover:cursor-pointer">Hybrid</SelectItem>
              </SelectContent>
            </Select>
          </FormField>

          <FormField label="Max Team Size" required>
            <Input
              type="number"
              min={2}
              value={formData.maxTeamSize}
              onChange={(e) => handleInputChange("maxTeamSize", e.target.value)}
              placeholder="e.g. 5"
              required
            />
          </FormField>

          <FormField label="Rules (PDF)">
            <input
              type="file"
              accept="application/pdf"
              onChange={(e) => setRulesFile(e.target.files?.[0] ?? null)}
              className="block w-full text-[13px] text-[#6e7891] file:mr-[12px] file:py-[6px] file:px-[12px] file:rounded-[8px] file:border-0 file:bg-[#0058ab] file:text-white file:text-[12px] hover:file:bg-[#004a94] cursor-pointer"
            />
            {rulesFile && <span className="text-[12px] text-[#6e7891]">{rulesFile.name} ({(rulesFile.size / 1024).toFixed(1)} KB)</span>}
          </FormField>

          <FormField label="Prizes">
            <div className="flex flex-col gap-[12px]">
              {prizes.map((prize, index) => (
                <div key={index} className="flex flex-col md:flex-row gap-2 md:gap-[8px] md:items-end">
                  <div className="flex-1 flex flex-col md:flex-row gap-2 md:gap-[8px]">
                    <Input value={prize.title} onChange={(e) => handlePrizeChange(index, "title", e.target.value)} placeholder="Title (e.g. Winner)" />
                    <Input type="number" value={prize.amount} onChange={(e) => handlePrizeChange(index, "amount", e.target.value)} placeholder="Amount" className="md:w-[120px]" />
                    <Select value={prize.currency} onValueChange={(v) => handlePrizeChange(index, "currency", v)}>
                      <SelectTrigger className="md:w-[90px]"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-gray-50">
                        <SelectItem value="INR">INR</SelectItem>
                        <SelectItem value="USD">USD</SelectItem>
                        <SelectItem value="EUR">EUR</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {prizes.length > 1 && (
                    <button type="button" onClick={() => removePrize(index)}
                      className="w-full md:w-auto px-[12px] py-[8px] bg-[#ff816e] text-white rounded text-[13px] hover:bg-[#ff6b56] transition-colors">
                      Remove
                    </button>
                  )}
                </div>
              ))}
              <button type="button" onClick={addPrize}
                className="px-[16px] py-[8px] bg-white border border-[#0058ab] text-[#0058ab] rounded-sm text-[13px] hover:bg-[#f0f5ff] transition-colors self-start">
                Add Prize
              </button>
            </div>
          </FormField>
        </FormSection>

        {/* Registration Phase */}
        <FormSection title="Registration Phase">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-[16px]">
            <FormField label="Start Date" required>
              <Input type="date" value={formData.registrationStart} onChange={(e) => handleInputChange("registrationStart", e.target.value)} required />
            </FormField>
            <FormField label="End Date" required>
              <Input type="date" value={formData.registrationEnd} onChange={(e) => handleInputChange("registrationEnd", e.target.value)} required />
            </FormField>
          </div>
        </FormSection>

        {/* Submission Phase */}
        <FormSection title="Submission Phase">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-[16px]">
            <FormField label="Phase Start Date" required>
              <Input type="date" value={formData.submissionStart} onChange={(e) => handleInputChange("submissionStart", e.target.value)} required />
            </FormField>
            <FormField label="Phase End Date" required>
              <Input type="date" value={formData.submissionEnd} onChange={(e) => handleInputChange("submissionEnd", e.target.value)} required />
            </FormField>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-[16px]">
            <FormField label="Initial Idea Submission End Date" required>
              <Input type="date" value={formData.submissionInitialEnd} onChange={(e) => handleInputChange("submissionInitialEnd", e.target.value)} required />
            </FormField>
            <FormField label="Final Submission End Date" required>
              <Input type="date" value={formData.submissionFinalEnd} onChange={(e) => handleInputChange("submissionFinalEnd", e.target.value)} required />
            </FormField>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-[16px]">
            <FormField label="Mentor Review Days">
              <Input
                type="number"
                value={formData.mentorReviewDays}
                onChange={(e) => handleInputChange("mentorReviewDays", e.target.value)}
                placeholder="e.g. 3"
              />
            </FormField>
            <FormField label="Idea Selection Deadline">
              <Input
                type="date"
                value={formData.ideaSelectionDeadline}
                onChange={(e) => handleInputChange("ideaSelectionDeadline", e.target.value)}
              />
            </FormField>
          </div>
        </FormSection>

        {/* Evaluation Phase */}
        <FormSection title="Evaluation Phase">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-[16px]">
            <FormField label="Start Date" required>
              <Input type="date" value={formData.evaluationStart} onChange={(e) => handleInputChange("evaluationStart", e.target.value)} required />
            </FormField>
            <FormField label="End Date" required>
              <Input type="date" value={formData.evaluationEnd} onChange={(e) => handleInputChange("evaluationEnd", e.target.value)} required />
            </FormField>
          </div>
        </FormSection>

        {/* Evaluation Rounds */}
        <FormSection title="Evaluation Rounds">
          <div className="flex flex-col gap-[16px]">
            {rounds.map((round, index) => (
              <div
                key={index}
                className="p-[24px] rounded-[16px] border border-[#e4e8ee] bg-white flex flex-col gap-[16px]"
              >
                <div className="flex items-center justify-between">
                  <h4 className="font-['Inter:Medium',sans-serif] font-medium leading-[21px] text-[#1a2332] text-[14px]">
                    Round {index + 1}
                  </h4>
                  {rounds.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeRound(index)}
                      className="px-[12px] py-[6px] bg-[#ff816e] text-white rounded-sm font-['Inter:Medium',sans-serif] font-medium text-[11px] hover:bg-[#ff6b56] transition-colors"
                    >
                      Remove
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 md:gap-[12px]">
                  <FormField label="Order" required>
                    <Input
                      type="number"
                      value={round.order}
                      onChange={(e) => handleRoundChange(index, "order", parseInt(e.target.value) || 0)}
                      required
                    />
                  </FormField>

                  <FormField label="Name" required>
                    <Input
                      value={round.name}
                      onChange={(e) => handleRoundChange(index, "name", e.target.value)}
                      placeholder="Round name"
                      required
                    />
                  </FormField>

                  <FormField label="Type" required>
                    <Input
                      value={round.type}
                      onChange={(e) => handleRoundChange(index, "type", e.target.value)}
                      placeholder="Round type"
                      required
                    />
                  </FormField>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-[12px]">
                  <FormField label="Start Date">
                    <Input type="date" value={round.startDate} onChange={(e) => handleRoundChange(index, "startDate", e.target.value)} />
                  </FormField>
                  <FormField label="End Date">
                    <Input type="date" value={round.endDate} onChange={(e) => handleRoundChange(index, "endDate", e.target.value)} />
                  </FormField>
                </div>
              </div>
            ))}

            <button
              type="button"
              onClick={addRound}
              className="px-[16px] py-[8px] bg-white border border-[#0058ab] text-[#0058ab] rounded-[12px] font-['Inter:Medium',sans-serif] font-medium text-[13px] hover:bg-[#f0f5ff] transition-colors self-start"
            >
              Add Round
            </button>
          </div>
        </FormSection>

        {/* Form Actions */}
        <div className="flex gap-[12px] justify-end pt-[24px] border-t border-[#e4e8ee]">
          {error && (
            <p className="text-red-600 text-sm flex-1 flex items-center">{error}</p>
          )}
          <button
            type="button"
            onClick={() => navigate("/admin-dashboard/manage-hackathon")}
            className="px-[24px] py-[12px] bg-white border border-[#6e7891] text-[#6e7891] rounded-[14px] font-['Inter:Medium',sans-serif] font-medium text-[14px] hover:bg-[#f5f7fb] transition-colors"
          >
            Cancel
          </button>
          <Button
            type="submit"
            disabled={submitting}
            className="px-[24px] py-[12px] bg-[#0058ab] text-white rounded-[14px] font-['Inter:Medium',sans-serif] font-medium text-[14px] shadow-[0px_4px_12px_0px_rgba(0,88,171,0.25)] hover:bg-[#004a94] transition-colors disabled:opacity-50"
          >
            {submitting ? "Saving..." : isEdit ? "Update Hackathon" : "Create Hackathon"}
          </Button>
        </div>
      </form>
      )}
    </div>
  );
}

function FormSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div
      className="rounded-[16px] border-[0.667px] border-[rgba(228,236,244,0.4)] shadow-[0px_8px_32px_0px_rgba(31,38,135,0.08)] p-[32px]"
      style={{
        backgroundImage:
          "linear-gradient(157.593deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.6) 100%)",
      }}
    >
      <h3 className="font-['Inter:Semi_Bold',sans-serif] font-semibold leading-[27px] text-[#1a2332] text-[18px] mb-[24px]">
        {title}
      </h3>
      <div className="flex flex-col gap-[20px]">{children}</div>
    </div>
  );
}

function FormField({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-[8px]">
      <Label className="font-['Inter:Medium',sans-serif] font-medium leading-[19.5px] text-[#1a2332] text-[13px]">
        {label}
        {required && <span className="text-[#ff816e] ml-[4px]">*</span>}
      </Label>
      {children}
    </div>
  );
}
